import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { isEqual } from 'lodash';
import { useShallow } from 'zustand/react/shallow';
import { useConfigurationStore } from '@store/configurationStore';
import { useQualificationFlowStore } from '@store/qualificationStore/qualificationStore';
import { useShoppingCart } from '@hooks/useShoppingCart/useShoppingCart';
import { getInitialSelections } from '@utils/cartSelections';
import { getCategoryFromItemName } from '@utils/cartUtils';
import { saveEquipmentChanges, saveAddOnChanges } from '@utils/editPlanSave/editPlanSave';
import { selectParsedEquipment } from '@utils/equipmentMapper';
import { determineEquipmentPopupAction } from '@utils/equipmentPopupLogic';
import type { CartItemDTO, ProductOfferingDTO, ShoppingCartDTO } from '@t/api.types';
import type { EditModeConfig } from '@t/editMode.types';
import type { PlanConfigurationState } from '@t/configuration.types';
import type { Plan } from '@t/productOffering.types';

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export type UseEditPlanReturn = {
  isEditing: boolean;
  isSaving: boolean;
  editConfig: EditModeConfig | null;
  startEdit: (cartItem: CartItemDTO) => void;
  handleSaveStep: () => Promise<void>;
  handleCancelEdit: () => void;
  hasUnsavedChanges: boolean;
};

export const useEditPlan = (sessionId: string): UseEditPlanReturn => {
  const { editMode, startEditMode, exitEditMode, setConfigError, openPopup, closePopup, saveAndClose, setStep } =
    useConfigurationStore(
      useShallow((s) => ({
        editMode: s.editMode,
        startEditMode: s.startEditMode,
        exitEditMode: s.exitEditMode,
        setConfigError: s.setConfigError,
        openPopup: s.openPopup,
        closePopup: s.closePopup,
        saveAndClose: s.saveAndClose,
        setStep: s.setStep,
      }))
    );

  const hasUnsavedChanges = useConfigurationStore((s) => {
    if (!s.originalSelections) return false;
    return (
      !isEqual(s.originalSelections.selectedEquipment, s.selectedEquipment) ||
      !isEqual(s.originalSelections.selectedAddOns, s.selectedAddOns)
    );
  });

  const resolvedAddressId = useQualificationFlowStore((s) => s.resolvedAddressId);

  const queryClient = useQueryClient();
  const {
    cart,
    selectAddOnsAsync,
    removeAddOnAsync,
    removeAddOnsAsync,
    isPending: isSaving,
  } = useShoppingCart(sessionId);

  const mutations = { selectAddOnsAsync, removeAddOnAsync, removeAddOnsAsync };

  // -- startEdit ---------------------------------------------------------------

  const startEdit = useCallback(
    (cartItem: CartItemDTO) => {
      const planId = cartItem.productOffering?.id;
      const planName = cartItem.productOffering?.name ?? '';
      if (!planId) return;

      const serviceType = getCategoryFromItemName(planName);
      if (!serviceType) return;

      const config: EditModeConfig = {
        isEditMode: true,
        planId,
        planName,
        offeringCode: planId,
        serviceType,
      };

      const homeId = resolvedAddressId ?? '';
      const plans = queryClient.getQueryData<Plan[]>(['productOfferings', sessionId, homeId]);
      const plan = plans?.find((p) => p.planId === planId);

      const equipmentDTOs = queryClient.getQueryData<ProductOfferingDTO[]>(['equipment', sessionId, homeId, planId]);
      const equipmentIds = new Set(equipmentDTOs?.map((e) => e.id).filter((id): id is string => id != null) ?? []);
      const initialSelections = getInitialSelections(cart, planId, plan?.addOns, equipmentIds);
      const equipmentAction = determineEquipmentPopupAction(selectParsedEquipment(equipmentDTOs ?? []));
      const planConfig: PlanConfigurationState = {
        planId,
        planName,
        planCategory: serviceType,
        parentOfferingCode: planId,
        homeIdOfQualifiedOffers: homeId,
        sessionId,
        hasEquipment: equipmentAction === 'show',
        hasAddOns: (plan?.addOns?.length ?? 0) > 0,
      };

      const snapshot = {
        ...planConfig,
        selectedEquipment: initialSelections?.equipment ?? null,
        selectedAddOns: initialSelections?.addOns ?? [],
        isComplete: true,
      };

      startEditMode(config, snapshot);
      openPopup(planConfig, {
        equipment: initialSelections?.equipment ?? null,
        addOns: initialSelections?.addOns,
      });
    },
    [cart, sessionId, resolvedAddressId, queryClient, startEditMode, openPopup]
  );

  // -- handleSaveStep (orchestrator only) --------------------------------------

  const handleSaveStep = useCallback(async () => {
    const {
      editMode: currentEditMode,
      activePlanConfig,
      currentStep,
      selectedEquipment,
      selectedAddOns,
      originalSelections,
    } = useConfigurationStore.getState();

    if (!currentEditMode || !activePlanConfig || !originalSelections) return;

    const reportError = (message: string) => {
      queryClient.invalidateQueries({ queryKey: ['shoppingCart', sessionId] });
      setConfigError(message);
    };

    const currentCart = queryClient.getQueryData<ShoppingCartDTO>(['shoppingCart', sessionId]);
    const parentItem = currentCart?.cartItem?.find((item) => item.productOffering?.id === currentEditMode.planId);
    const isEquipmentStep = currentStep === 'equipment';

    // ── Equipment step ──────────────────────────────────────────────────
    const advanceAfterEquipment = () => {
      if (activePlanConfig.hasAddOns) {
        setStep('addOns');
      } else {
        saveAndClose();
        exitEditMode();
      }
    };

    if (isEquipmentStep) {
      if (isEqual(selectedEquipment, originalSelections.selectedEquipment)) {
        advanceAfterEquipment();
        return;
      }

      try {
        await saveEquipmentChanges({
          parentItem,
          originalEquipment: originalSelections.selectedEquipment,
          selectedEquipment,
          offeringCode: currentEditMode.offeringCode,
          equipment:
            queryClient.getQueryData<ProductOfferingDTO[]>([
              'equipment',
              sessionId,
              activePlanConfig.homeIdOfQualifiedOffers,
              currentEditMode.planId,
            ]) ?? [],
          mutations,
        });
      } catch (error) {
        console.error('[useEditPlan] Equipment save failed:', error);
        reportError('Failed to update equipment. Please try again.');
        return;
      }

      advanceAfterEquipment();
      return;
    }

    // ── Add-ons step (final) ────────────────────────────────────────────
    const homeId = activePlanConfig.homeIdOfQualifiedOffers;
    const plans = queryClient.getQueryData<Plan[]>(['productOfferings', sessionId, homeId]);
    const plan = plans?.find((p) => p.planId === currentEditMode.planId);

    try {
      await saveAddOnChanges({
        parentItem,
        offeringCode: currentEditMode.offeringCode,
        originalAddOns: originalSelections.selectedAddOns,
        currentAddOns: selectedAddOns,
        planAddOns: plan?.addOns,
        mutations,
      });
    } catch (error) {
      console.error('[useEditPlan] Add-ons save failed:', error);
      reportError('Failed to update add-ons. Please try again.');
      return;
    }

    saveAndClose();
    exitEditMode();
  }, [
    sessionId,
    queryClient,
    selectAddOnsAsync,
    removeAddOnAsync,
    removeAddOnsAsync,
    setStep,
    saveAndClose,
    exitEditMode,
    setConfigError,
  ]);

  // -- handleCancelEdit --------------------------------------------------------

  const handleCancelEdit = useCallback(() => {
    exitEditMode();
    closePopup();
  }, [exitEditMode, closePopup]);

  return {
    isEditing: editMode !== null,
    isSaving,
    editConfig: editMode,
    startEdit,
    handleSaveStep,
    handleCancelEdit,
    hasUnsavedChanges,
  };
};
